import { api } from '../../utils/api'
// Add authentication interceptor
const videoApi = api;
videoApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * Get Twilio access token for video room
 * @param {string} roomId - Room ID
 * @param {boolean} isInterviewer - Is user interviewer
 * @param {Object} identityData - Identity data for unique Twilio identity
 * @returns {Promise<Object>} Token and room info
 */
export async function getTwilioToken(roomId, isInterviewer, identityData = {}) {
  try {
    const response = await videoApi.post('/video/call/twilio/start', {
      roomId,
      isInterviewer,
      identityData // { userId, candidateId, name }
    })
    return response.data
  } catch (error) {
    console.error('[Twilio Service] Get token error:', error)
    throw error
  }
}

/**
 * Start Twilio recording with full context (legacy method)
 * @param {Object} params - Start recording parameters
 * @param {string} params.roomId - Room ID
 * @param {boolean} params.isInterviewer - Is user interviewer
 * @param {Object} params.owner - Owner (company user) information
 * @param {Object} params.candidate - Candidate (jobseeker) information
 * @param {string} params.jobId - Job ID
 * @param {string} params.jobTitle - Job title
 * @param {string} params.interviewType - Interview type
 * @returns {Promise<Object>} Token and room info
 */
export async function startTwilioRecording({
  roomId,
  isInterviewer,
  owner,
  candidate,
  jobId,
  jobTitle,
  interviewType
}) {
  try {
    const response = await videoApi.post('/video/call/twilio/start', {
      roomId,
      isInterviewer,
      owner,
      candidate,
      jobId,
      jobTitle,
      interviewType
    })
    return response.data
  } catch (error) {
    console.error('[Twilio Service] Start recording error:', error)
    throw error
  }
}

/**
 * Stop Twilio recording
 * @param {string} roomId - Room ID
 * @param {Array} transcriptEntries - Transcript entries (optional)
 * @returns {Promise<Object>} Recording info
 */
export async function stopTwilioRecording(roomId, transcriptEntries = []) {
  try {
    const response = await videoApi.post('/video/call/twilio/stop', {
      roomId,
      transcriptEntries,
    })
    return response.data
  } catch (error) {
    console.error('[Twilio Service] Stop recording error:', error)
    throw error
  }
}

/**
 * Get recording status
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Recording status
 */
export async function getRecordingStatus(roomId) {
  try {
    const response = await videoApi.get(`/video/call/twilio/status/${roomId}`)
    return response.data
  } catch (error) {
    console.error('[Twilio Service] Get status error:', error)
    throw error
  }
}

/**
 * Manually download recordings
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Download result
 */
export async function downloadRecordings(roomId) {
  try {
    const response = await videoApi.post(`/video/call/twilio/download-recordings/${roomId}`)
    return response.data
  } catch (error) {
    console.error('[Twilio Service] Download recordings error:', error)
    throw error
  }
}

/**
 * Test Twilio configuration
 * @returns {Promise<Object>} Configuration status
 */
export async function testTwilioConfig() {
  try {
    const response = await videoApi.get('/video/call/twilio/test')
    return response.data
  } catch (error) {
    console.error('[Twilio Service] Test config error:', error)
    throw error
  }
}

export default {
  getTwilioToken,
  startTwilioRecording,
  stopTwilioRecording,
  getRecordingStatus,
  downloadRecordings,
  testTwilioConfig,
}
